import * as Sentry from '@sentry/react';

import { type ImageType } from '@common/ui/PicturesUpload';
import { compressImage } from '@common/utils';

import { usePostUpload } from '@services/hooks/FilesService';

export const ERROR_PHOTO_TITLE = 'Ошибка загрузки фотографии';
export const UNKNOWN_PHOTO_ERROR_MESSAGE =
  'Не удалось загрузить фотографию. Попробуйте еще раз.';

interface UploaderOptions {
  onUnknownError?: (e: Error) => void;
  onFileTooLarge?: ({ message }: { message: string }) => void;
  maxFileSize?: number;
}

const uploadWithRetry = async (
  upload: (params: { file: File }) => Promise<{ url: string }>,
  file: File,
  maxRetries = 3,
): Promise<string | null> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await upload({ file });

      if (!response?.url) {
        throw new Error('Upload returned empty URL');
      }

      return response.url;
    } catch (error) {
      console.warn(`Upload attempt ${attempt}/${maxRetries} failed:`, error);

      if (attempt === maxRetries) {
        throw error;
      }

      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return null;
};

const resolveUploadErrorMessage = (rawError: unknown) => {
  if (!(rawError instanceof Error)) {
    return 'Ошибка загрузки файла';
  }

  if (
    rawError.message.includes('413') ||
    rawError.message.includes('too large')
  ) {
    return 'Файл слишком большой для загрузки';
  }

  if (
    rawError.message.includes('415') ||
    rawError.message.includes('unsupported')
  ) {
    return 'Неподдерживаемый формат файла';
  }

  if (
    rawError.message.includes('network') ||
    rawError.message.includes('timeout')
  ) {
    return 'Ошибка сети. Проверьте подключение к интернету';
  }

  if (
    rawError.message.includes('слишком большой') ||
    rawError.message.includes('PDF файлы не поддерживаются') ||
    rawError.message.includes('Видео файлы не поддерживаются') ||
    rawError.message.includes('Неподдерживаемый формат файла')
  ) {
    return rawError.message;
  }

  return 'Ошибка загрузки файла';
};

const isFileTooLargeMessage = (message: string) => {
  return message.includes('слишком большой') || message.includes('too large');
};

export const useUploader = ({
  onUnknownError,
  onFileTooLarge,
  maxFileSize = 10, // MB
}: UploaderOptions = {}) => {
  const { trigger: triggerPostUpload } = usePostUpload();

  const uploader = async (image: ImageType) => {
    const file = image.file;

    if (!file) {
      onUnknownError?.(new Error('File not found'));
      return null;
    }

    try {
      if (file.size > maxFileSize * 1024 * 1024) {
        const errorMessage = `Файл слишком большой, максимум ${maxFileSize} МБ`;
        onFileTooLarge?.({
          message: errorMessage,
        });
        return null;
      }

      //TODO перенести все валидации файлов в одно какое то место

      const compressedFile = await compressImage(file);
      const responseUrl = await uploadWithRetry(
        triggerPostUpload,
        compressedFile,
      );

      if (!responseUrl) {
        Sentry.captureException(
          new Error('Photo response without url after retries'),
          {
            tags: { section: 'photo-upload' },
            extra: {
              fileName: file.name,
              size: file.size,
              type: file.type,
            },
          },
        );
      }

      return responseUrl;
    } catch (rawError) {
      const errorMessage = resolveUploadErrorMessage(rawError);
      const processedError: Error & { cause?: unknown } = new Error(
        errorMessage,
      );
      processedError.cause = rawError;

      Sentry.captureException(processedError, {
        tags: { section: 'photo-upload' },
        extra: {
          fileName: file.name,
          size: file.size,
          type: file.type,
          cause: processedError?.cause,
        },
      });

      if (isFileTooLargeMessage(errorMessage)) {
        onFileTooLarge?.({ message: errorMessage });
      } else {
        onUnknownError?.(processedError);
      }

      return null;
    }
  };

  return uploader;
};
